import { CFG_MAX_STEPS, CFG_MIN_STEP_MS } from '../../protocol/constants.ts';
import type { JigStep } from '../../protocol/types.ts';

export const PATH_WIDTH = 640;
export const PATH_HEIGHT = 400;
export const MAX_WAYPOINTS = 64;
/** Short moves approximate straight lines without exhausting the 256-step budget too quickly. */
const MOVE_PX = 16;
const CLICK_MS = 50;

export const PATH_ACTIONS = {
  none: 'Move only',
  'left-click': 'Left click',
  'right-click': 'Right click',
  'middle-click': 'Middle click',
  'drag-start': 'Start left drag',
  'drag-end': 'End left drag',
} as const;
export type PathAction = keyof typeof PATH_ACTIONS;

export interface Waypoint {
  x: number;
  y: number;
  /** Performed on arrival, then pauseMs elapses before travelling onward. */
  action: PathAction;
  pauseMs: number;
}

export interface PathResult {
  steps: JigStep[];
  problems: string[];
  requiredSteps: number;
  playbackMs: number;
  pathLengthPx: number;
}

export function waypoint(x = 0, y = 0): Waypoint {
  return { x, y, action: 'none', pauseMs: 0 };
}

/** Viewport coordinates → fixed grid, including SVG's centered letterboxing. */
export function pointOnGrid(
  clientX: number, clientY: number,
  rect: { left: number; top: number; width: number; height: number },
): { x: number; y: number } | null {
  const scale = Math.min(rect.width / PATH_WIDTH, rect.height / PATH_HEIGHT);
  if (!Number.isFinite(scale) || scale <= 0 || !Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
  return {
    x: Math.round(Math.max(-PATH_WIDTH / 2, Math.min(PATH_WIDTH / 2, (clientX - rect.left - rect.width / 2) / scale))),
    y: Math.round(Math.max(-PATH_HEIGHT / 2, Math.min(PATH_HEIGHT / 2, (clientY - rect.top - rect.height / 2) / scale))),
  };
}

/** Compile geometry, not captured events: never resample across a waypoint or action. */
export function compilePath(points: readonly Waypoint[], speed: number): PathResult {
  const problems: string[] = [];
  if (!Number.isInteger(speed) || speed < 1 || speed > 2000) {
    problems.push('Speed must be a whole number from 1 to 2000 px/s.');
  }
  if (points.length < 1 || points.length > MAX_WAYPOINTS) {
    problems.push(`Use 1–${MAX_WAYPOINTS} points, including the fixed start.`);
  }
  if (points[0]?.x !== 0 || points[0]?.y !== 0) problems.push('The first point must stay at (0, 0).');
  let dragging = false;
  for (const [i, point] of points.entries()) {
    const label = `Point ${i + 1}`;
    if (!Number.isInteger(point.x) || !Number.isInteger(point.y) ||
        Math.abs(point.x) > PATH_WIDTH / 2 || Math.abs(point.y) > PATH_HEIGHT / 2) {
      problems.push(`${label}: coordinates must be whole numbers inside the grid.`);
    }
    if (!Number.isInteger(point.pauseMs) || point.pauseMs < 0 || point.pauseMs > 60_000) {
      problems.push(`${label}: pause must be 0–60000 ms.`);
    }
    if (!Object.hasOwn(PATH_ACTIONS, point.action)) {
      problems.push(`${label}: unknown action.`);
    } else if (point.action === 'drag-start') {
      if (dragging) problems.push(`${label}: end the current drag before starting another.`);
      dragging = true;
    } else if (point.action === 'drag-end') {
      if (!dragging) problems.push(`${label}: start a drag before ending it.`);
      dragging = false;
    } else if (point.action !== 'none' && dragging) {
      problems.push(`${label}: end the drag before adding a click.`);
    }
  }
  if (dragging) problems.push('Add an End left drag action before using the path.');
  if (!points.some((p) => p.x !== 0 || p.y !== 0 || p.action !== 'none' || p.pauseMs > 0)) {
    problems.push('Add a point, action or pause to create a pattern.');
  }
  const empty: PathResult = { steps: [], problems, requiredSteps: 0, playbackMs: 0, pathLengthPx: 0 };
  if (problems.length) return empty;

  // Each step is an arrival; its delay is the wait BEFORE the next arrival.
  const events: JigStep[] = [{ dx: 0, dy: 0, wheel: 0, buttons: 0, delayMs: 0 }];
  let buttons = 0;
  let x = 0;
  let y = 0;
  let pathLengthPx = 0;
  function wait(ms: number) { events[events.length - 1]!.delayMs += ms; }
  function emit(dx = 0, dy = 0) { events.push({ dx, dy, wheel: 0, buttons, delayMs: 0 }); }
  function moveTo(tx: number, ty: number) {
    const dx = tx - x;
    const dy = ty - y;
    const distance = Math.hypot(dx, dy);
    if (distance === 0) return;
    pathLengthPx += distance;
    const parts = Math.ceil(distance / MOVE_PX);
    const duration = Math.round(distance / speed * 1000);
    let previousX = 0;
    let previousY = 0;
    let previousTime = 0;
    for (let part = 1; part <= parts; part++) {
      // Cumulative rounding preserves signed endpoints exactly, including diagonals.
      const nextX = Math.round(dx * part / parts);
      const nextY = Math.round(dy * part / parts);
      const nextTime = Math.round(duration * part / parts);
      wait(nextTime - previousTime);
      emit(nextX - previousX, nextY - previousY);
      previousX = nextX;
      previousY = nextY;
      previousTime = nextTime;
    }
    x = tx;
    y = ty;
  }

  for (const point of points) {
    moveTo(point.x, point.y);
    switch (point.action) {
      case 'left-click':
      case 'right-click':
      case 'middle-click':
        buttons = point.action === 'left-click' ? 1 : point.action === 'right-click' ? 2 : 4;
        emit();
        wait(CLICK_MS);
        buttons = 0;
        emit();
        wait(CLICK_MS);
        break;
      case 'drag-start': buttons = 1; emit(); break;
      case 'drag-end': buttons = 0; emit(); break;
      case 'none': break;
    }
    wait(point.pauseMs);
  }
  // Macros loop. Return with buttons UP so repeats neither drift nor drag back.
  moveTo(0, 0);

  const output: JigStep[] = [];
  for (const event of events) {
    const dwell = Math.max(CFG_MIN_STEP_MS, event.delayMs);
    const chunks = Math.ceil(dwell / 0xffff);
    for (let i = 0; i < chunks; i++) {
      output.push({
        ...event,
        dx: i === 0 ? event.dx : 0,
        dy: i === 0 ? event.dy : 0,
        delayMs: Math.round(dwell * (i + 1) / chunks) - Math.round(dwell * i / chunks),
      });
    }
  }
  if (output.length > CFG_MAX_STEPS) {
    problems.push(`This path needs ${output.length} steps; the device holds ${CFG_MAX_STEPS}. Shorten the path or remove points/actions. Nothing will be truncated.`);
  }
  return {
    steps: problems.length ? [] : output,
    problems,
    requiredSteps: output.length,
    playbackMs: output.reduce((total, step) => total + step.delayMs, 0),
    pathLengthPx,
  };
}
